/**
 * Valid app names for MoEngage tracking
 * These are the normalized app names that will be sent to MoEngage
 */
export type AppName = 'VIPA' | 'KIDS' | 'ALTV' | 'ThaiPBS';

/**
 * Normalizes an application name to the standard app name format
 * Used for MoEngage tracking attributes (signupFromApp and app event attribute)
 *
 * Examples:
 * - "VIPA Web", "VIPA Android", "VIPA iOS" → "VIPA"
 * - "ThiapbsKids", "ThaiPBS Kids", "KIDS_WEB" → "KIDS"
 * - "ThaiPBS web", "ThaiPBS", "THAI_PBS" → "ThaiPBS"
 * - "ALTV xxx" → "ALTV"
 *
 * @param applicationName - The application name from Logto (e.g., "VIPA Web", "ThiapbsKids")
 * @returns The normalized app name or undefined if not recognized
 */
export const normalizeAppName = (applicationName: string | undefined): AppName | undefined => {
  if (!applicationName) {
    return undefined;
  }

  const lowerName = applicationName.toLowerCase().replace(/[\s_-]/g, '');

  // Check for KIDS first (before ThaiPBS check to avoid matching "thaipbskids" as ThaiPBS)
  if (lowerName.includes('kids')) {
    return 'KIDS';
  }
  // Check for VIPA
  if (lowerName.includes('vipa')) {
    return 'VIPA';
  }
  // Check for ALTV
  if (lowerName.includes('altv')) {
    return 'ALTV';
  }
  // Check for ThaiPBS
  if (lowerName.includes('thaipbs')) {
    return 'ThaiPBS';
  }

  return undefined;
};

/**
 * Gets the normalized app name with an optional fallback
 *
 * @param applicationName - The application name from Logto
 * @param fallback - Optional fallback value if application name is not recognized
 * @returns The normalized app name or the fallback value
 */
export const getAppName = (
  applicationName: string | undefined,
  fallback?: string
): AppName | string | undefined => {
  return normalizeAppName(applicationName) ?? fallback;
};
